import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n/I18nContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Lock, User, Mail, Loader2, UserCog } from "lucide-react";
import ProfileForm from "../components/ProfileForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const { t, language } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t("passwords_not_match"));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t("password_min_length"));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, getAuthHeader());
      toast.success(t("password_changed_success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("password_change_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      <header className="bg-neutral-900 border-b border-neutral-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(user?.role === "admin" ? "/admin" : "/dashboard")}
            className="text-neutral-400 hover:text-white hover:bg-white/10"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {user?.role === "admin" ? "Admin Paneline Dön" : t("go_dashboard_back")}
          </Button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-bold text-white font-['Nunito']" data-testid="account-title">
          {t("my_account")}
        </h1>

        {/* User Info Card */}
        <Card className="border-0 shadow-lg bg-neutral-900 border-neutral-800">
          <CardHeader>
            <CardTitle className="text-white text-lg font-['Nunito']">{t("account_info")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-neutral-400" />
              <span className="text-neutral-300">{user?.full_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-neutral-400" />
              <span className="text-neutral-300">{user?.email}</span>
            </div>
          </CardContent>
        </Card>

        {/* Profile Card (user can edit their own profile) */}
        {user?.role !== "admin" && (
          <Card className="border-0 shadow-lg bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white text-lg font-['Nunito'] flex items-center gap-2">
                <UserCog className="w-5 h-5" />
                {({tr:"Profil Bilgilerim",de:"Mein Profil",it:"Il mio profilo",es:"Mi perfil",fr:"Mon profil",ru:"Мой профиль",cnr:"Moj profil",sr:"Moj profil",sq:"Profili im",el:"Το προφίλ μου"}[language]) || "My Profile"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProfileForm
                initialProfile={user?.profile || null}
                submitLabelKey="save"
              />
            </CardContent>
          </Card>
        )}

        {/* Change Password Card */}
        <Card className="border-0 shadow-lg bg-neutral-900 border-neutral-800">
          <CardHeader>
            <CardTitle className="text-white text-lg font-['Nunito'] flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {t("change_password")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-300">{t("current_password")}</Label>
                <div className="relative">
                  <Input
                    type={showCurrent ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="bg-neutral-800 border-neutral-700 text-white pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">{t("new_password")}</Label>
                <div className="relative">
                  <Input
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="bg-neutral-800 border-neutral-700 text-white pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">{t("password_confirm")}</Label>
                <Input
                  type={showNew ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={saving}
                data-testid="change-password-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t("change_password")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
